/**
 * Template Specification for "dualBar" Chart
 *
 * This template defines the structure and default configuration for a "dual_bar" chart.
 *
 * Key Categories:
 * 1. Standard Fields:
 *    - 'type': Specifies the chart type (here, "dual_bar").
 *    - 'title', 'bar1_metric', 'subtitle_1', 'bar2_metric', and 'subtitle_2':
 *      These fields expect dynamic inputs. Fields starting with "$" indicate that their values
 *      will be provided by the user or computed at runtime.
 *
 * 2. Label Fields:
 *    - Keys prefixed with a single underscore (e.g., '_type', '_title', '_bar1_metric', '_subtitle_1',
 *      '_bar2_metric', '_subtitle_2') provide human-readable labels for their corresponding fields.
 *
 * 3. Default and Dependency Fields:
 *    - Keys with double underscores (e.g., '__title', '__subtitle_1', '__subtitle_2'):
 *         - A value starting with "%" (e.g., '__title': '%Dual Bar') indicates a default value for that field,
 *           with the "%" stripped to yield the default (in this case, "Dual Bar").
 *         - A value starting with "$$" (e.g., '__subtitle_1': '$$bar1_metric' and '__subtitle_2': '$$bar2_metric')
 *           sets up a dependency, where the subtitle for each bar dynamically reflects the metric name
 *           associated with the corresponding bar metric field.
 *
 * This specification enables the creation of dual bar charts with two distinct metric selections,
 * associated subtitles that update based on the selected metrics, and customizable titles. It
 * provides a flexible framework for initializing and configuring dual bar charts.
 */
export const dualBar = {
    'type': 'grouped_bar_chart',
    '_type': 'Grouped Bar',
    'title': '$string',
    '_title': 'Title',
    '__title': '%Dual Bar',
    'bar1_metric': '$metric',
    '_bar1_metric': 'Bar 1 Metric',
    'subtitle_1': '$string',
    '_subtitle_1': 'Bar 1 Metric Subtitle',
    '__subtitle_1': '$$bar1_metric',
    'bar2_metric': '$metric',
    '_bar2_metric': 'Bar 2 Metric',
    'subtitle_2': '$string',
    '_subtitle_2': 'Bar 2 Metric Subtitle',
    '__subtitle_2': '$$bar2_metric',
}

// Example chart configuration for "dual_bar" chart. Used in preview mode
export const dualBarExample = (title, metric_label1, metric_label2,primaryColor,secondaryColor) => ({
    chart: {
        type: "column", // change to "bar" for horizontal bars if desired
        zoomType: "xy",
        animation: false,
        events: {},
        height: "100%"
    },
    title: {
        text: title
    },
    xAxis: {
        categories: [
            "03-04",
            "03-06",
            "03-08"
        ],
        gridLineWidth: 2,
        lineColor: "#ccd6eb",
        lineWidth: 1,
        labels: {
            rotation: 0
        },
        tickmarkPlacement: "between",
        tickWidth: 1
    },
    yAxis: [
        {
            title: {
                text: metric_label1
            },
            tickAmount: 6,
            // Optional: add plotLines or other styling here if needed
        },
        {
            title: {
                text: metric_label2
            },
            min: 0,
            opposite: true,
            gridLineWidth: 0
        }
    ],
    legend: {
        enabled: true,
        verticalAlign: "top",
        layout: "horizontal"
    },
    tooltip: {
        shared: true,
        outside: true
    },
    plotOptions: {
        column: {
            borderWidth: 0,
            groupPadding: 0.05,
            pointPadding: 0.05,
            dataLabels: {
                enabled: true,
                inside: true,
                y: 0,
                rotation: 0,
                style: {
                    textOutline: "none"
                }
            }
        },
        series: {
            animation: false,
            marker: {
                enabled: true
            },
            events: {}
        }
    },
    colors: [
        primaryColor,
        secondaryColor,
        "#3B7EA1",
        "#C4820E",
        "#4797ff",
        "#f526fA"
    ],
    series: [
        {
            name: metric_label1,
            type: "column",
            yAxis: 0,
            data: [
                {
                    y: 14.8,
                    color: primaryColor
                },
                {
                    y: 16.4,
                    color: primaryColor
                },
                {
                    y: 15.6,
                    color: primaryColor
                }
            ],
            colorByPoint: false,
            dataLabels: {
                allowOverlap: true,
                style: {
                    textOutline: "none"
                }
            }
        },
        {
            name: metric_label2,
            type: "column",
            yAxis: 1,
            data: [
                {
                    y: 14.0,
                    color: secondaryColor
                },
                {
                    y: 16.4,
                    color: secondaryColor
                },
                {
                    y: 15.2,
                    color: secondaryColor
                }
            ],
            dataLabels: {
                enabled: true,
                style: {
                    textOutline: "none"
                }
            }
        }
    ],
    exporting: {
        enabled: false
    }
});
